import {
  api,
  changePasswordInput,
  changeTransactionPinInput,
  type ChangePasswordInput,
  type ChangeTransactionPinInput,
} from '@alpha/api-client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { PageHeader } from '../../components/PageHeader';
import { useSession } from '../../hooks/useSession';
import { Alert, Button, Card, Chip, FormGroup, Input } from '../../ui';
import { PROFILE_TABS } from './Profile';

export function Security() {
  const { user } = useSession();

  const form = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordInput),
    defaultValues: { current_password: '', password: '', password_confirmation: '' },
  });

  const change = useMutation({
    mutationFn: (values: ChangePasswordInput) => api.users.changePassword(values),
    onSuccess: () => form.reset(),
  });

  const pinForm = useForm<ChangeTransactionPinInput>({
    resolver: zodResolver(changeTransactionPinInput),
    defaultValues: { current_pin: '', current_password: '', pin: '', pin_confirmation: '' },
  });

  const changePin = useMutation({
    mutationFn: (values: ChangeTransactionPinInput) => api.users.changeTransactionPin(values),
    onSuccess: () => pinForm.reset(),
  });

  const enable2fa = useMutation({ mutationFn: () => api.users.enableTwoFactor() });

  return (
    <>
      <PageHeader title="Security" tabs={PROFILE_TABS} />

      <Card>
        <h3 className="card-title">Change Password</h3>
        {change.isSuccess ? <Alert tone="success">Password changed.</Alert> : null}
        {change.error ? <Alert tone="danger">{(change.error as Error).message}</Alert> : null}

        <form onSubmit={form.handleSubmit((values) => change.mutate(values))} noValidate>
          <FormGroup label="Current Password" error={form.formState.errors.current_password?.message}>
            <Input type="password" autoComplete="current-password" {...form.register('current_password')} />
          </FormGroup>
          <FormGroup label="New Password" error={form.formState.errors.password?.message}>
            <Input type="password" autoComplete="new-password" {...form.register('password')} />
          </FormGroup>
          <FormGroup label="Confirm New Password" error={form.formState.errors.password_confirmation?.message}>
            <Input type="password" autoComplete="new-password" {...form.register('password_confirmation')} />
          </FormGroup>
          <Button type="submit" variant="gold" disabled={change.isPending}>
            {change.isPending ? 'Updating…' : 'Update Password'}
          </Button>
        </form>
      </Card>

      <Card>
        <h3 className="card-title">Change Transaction PIN</h3>
        <p className="text-muted small">
          Your transaction PIN is used to authorise withdrawals. Verify with your current PIN, or your login
          password if you have not set one yet.
        </p>
        {changePin.isSuccess ? <Alert tone="success">Transaction PIN updated.</Alert> : null}
        {changePin.error ? <Alert tone="danger">{(changePin.error as Error).message}</Alert> : null}

        <form onSubmit={pinForm.handleSubmit((values) => changePin.mutate(values))} noValidate>
          <FormGroup
            label="Current PIN"
            hint="Leave blank if you don't have one yet"
            error={pinForm.formState.errors.current_pin?.message}
          >
            <Input inputMode="numeric" maxLength={8} {...pinForm.register('current_pin')} />
          </FormGroup>
          <FormGroup
            label="Or Current Password"
            error={pinForm.formState.errors.current_password?.message}
          >
            <Input type="password" autoComplete="current-password" {...pinForm.register('current_password')} />
          </FormGroup>
          <FormGroup label="New PIN (4-8 digits)" error={pinForm.formState.errors.pin?.message}>
            <Input inputMode="numeric" maxLength={8} autoComplete="off" {...pinForm.register('pin')} />
          </FormGroup>
          <FormGroup
            label="Confirm New PIN"
            error={pinForm.formState.errors.pin_confirmation?.message}
          >
            <Input inputMode="numeric" maxLength={8} autoComplete="off" {...pinForm.register('pin_confirmation')} />
          </FormGroup>
          <Button type="submit" variant="gold" disabled={changePin.isPending}>
            {changePin.isPending ? 'Updating…' : 'Update PIN'}
          </Button>
        </form>
      </Card>

      <Card>
        <h3 className="card-title">
          Two-Factor Authentication{' '}
          {user?.two_factor_enabled ? (
            <Chip tone="success">Enabled</Chip>
          ) : (
            <Chip tone="warning">Disabled</Chip>
          )}
        </h3>
        <p className="text-muted small">
          Adds a time-based one-time code to every login. Strongly recommended for accounts holding a
          withdrawable balance.
        </p>

        {enable2fa.data ? (
          <div className="totp-setup" dangerouslySetInnerHTML={{ __html: enable2fa.data.data.qr_svg }} />
        ) : null}

        <Button variant="outline-gold" onClick={() => enable2fa.mutate()} disabled={enable2fa.isPending}>
          {user?.two_factor_enabled ? 'Reconfigure 2FA' : 'Enable 2FA'}
        </Button>
      </Card>
    </>
  );
}
